import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import {
  FileText, FilePlus2, BookOpen, Sparkles, TrendingUp, MessageSquareQuote,
  ArrowRight, ArrowLeft, Trash2, Check, User,
} from "lucide-react";
import "@/richtext.css";

// Icon per shipped starter (custom templates fall back to a generic doc icon).
const TEMPLATE_ICONS = {
  "collectors-guide": BookOpen,
  "brand-spotlight": Sparkles,
  "market-report": TrendingUp,
  "interview": MessageSquareQuote,
};

/**
 * Dialog shown when starting a new Journal article. Lets the editor begin from
 * a blank article, preview a starter/custom template, then apply it. Editors can
 * also delete their own (custom) templates here. Calls onPick(template|null).
 */
export default function TemplatePicker({ open, onOpenChange, onPick }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);   // template being previewed (null = grid)
  const [toDelete, setToDelete] = useState(null);  // custom template pending delete

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    return api.get("/admin/journal/templates")
      .then((r) => setTemplates(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Couldn't load templates — you can still start blank"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    fetchTemplates();
  }, [open, fetchTemplates]);

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/journal/templates/${toDelete.id}`);
      toast.success("Template deleted");
      setTemplates((prev) => prev.filter((t) => t.id !== toDelete.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete template");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-picker-dialog">
        {!preview ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={18} className="text-[#c9a86a]" /> Start a new article
              </DialogTitle>
              <DialogDescription>
                Begin from a blank page or preview a starter layout to write faster. You can change everything after.
              </DialogDescription>
            </DialogHeader>

            <div className="grid sm:grid-cols-2 gap-3 mt-1">
              {/* Blank article */}
              <button
                type="button"
                onClick={() => onPick(null)}
                data-testid="template-pick-blank"
                className="group text-left rounded-lg border border-dashed border-[var(--border-lch)] bg-[var(--panel)] p-4 transition-colors hover:border-[#c9a86a] hover:bg-[var(--panel-2)]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a86a]"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <FilePlus2 size={18} className="text-[#c9a86a]" />
                  <span className="font-medium">Blank article</span>
                </div>
                <p className="text-xs text-[var(--muted)]">A clean slate — write from scratch.</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-[#9a7b3f] opacity-0 group-hover:opacity-100 transition-opacity">
                  Start <ArrowRight size={12} />
                </span>
              </button>

              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-[var(--border-lch)] bg-[var(--panel)] p-4 animate-pulse h-[116px]" data-testid="template-skeleton" />
                ))
              ) : (
                templates.map((t) => {
                  const Icon = TEMPLATE_ICONS[t.id] || FileText;
                  const isCustom = t.source === "custom";
                  return (
                    <div
                      key={t.id}
                      className="group relative text-left rounded-lg border border-[var(--border-lch)] bg-[var(--panel)] p-4 transition-colors hover:border-[#c9a86a] hover:bg-[var(--panel-2)]/60"
                    >
                      {isCustom && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setToDelete(t); }}
                          data-testid={`template-delete-${t.id}`}
                          aria-label={`Delete ${t.name}`}
                          className="absolute top-2 right-2 rounded-md p-1 text-[var(--muted)] hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setPreview(t)}
                        data-testid={`template-pick-${t.id}`}
                        className="block w-full text-left focus-visible:outline-none"
                      >
                        <div className="flex items-center gap-2 mb-1.5 pr-6">
                          <Icon size={18} className="text-[#c9a86a] shrink-0" />
                          <span className="font-medium truncate">{t.name}</span>
                        </div>
                        <p className="text-xs text-[var(--muted)] line-clamp-3 min-h-[2.5rem]">{t.description || "Custom template"}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] uppercase tracking-wide text-[var(--text-2)]">{t.category || "—"}</span>
                            {isCustom && <Badge variant="outline" className="text-[10px] py-0 border-[#e3cf9e] text-[#7a5f22] bg-[#f3e7cb]">Custom</Badge>}
                          </div>
                          <span className="inline-flex items-center gap-1 text-xs text-[#9a7b3f] opacity-0 group-hover:opacity-100 transition-opacity">
                            Preview <ArrowRight size={12} />
                          </span>
                        </div>
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="template-picker-cancel">Cancel</Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={18} className="text-[#c9a86a]" /> {preview.name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                <span className="uppercase tracking-wide text-[11px] text-[var(--text-2)]">{preview.category || "—"}</span>
                {preview.source === "custom" && preview.created_by_name && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[var(--muted)]"><User size={11} /> {preview.created_by_name}</span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-[var(--border-lch)] bg-[var(--panel)] p-5 max-h-[52vh] overflow-y-auto" data-testid="template-preview">
              <h1 className="text-2xl font-semibold leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                {preview.title || "Untitled"}
              </h1>
              {preview.excerpt && <p className="text-sm text-[var(--text-2)] italic mb-4">{preview.excerpt}</p>}
              <div className="journal-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.body || "<p class='text-[var(--muted)]'>This template has no body content.</p>") }} />
            </div>

            <div className="flex items-center justify-between mt-4">
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="template-preview-back">
                <ArrowLeft size={15} className="mr-1.5" /> Back to templates
              </Button>
              <Button
                onClick={() => onPick(preview)}
                data-testid="template-preview-use"
                className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]"
              >
                <Check size={15} className="mr-1.5" /> Use this template
              </Button>
            </div>
          </>
        )}
      </DialogContent>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.name}" will be removed for all editors. Articles already created from it are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="template-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
